import { useState } from 'react';
import { Divider, Stack } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { LinkButton } from '~/domains/design-system/Button';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';

import type { FlexibleDoublePicksModalProps, SlateConfiguration } from './types';
import { SlateConfigurationRow } from './SlateConfigurationRow';
import {
  filterCompleteSlates,
  getNextSlateValue,
  checkCanAddSlate,
  checkCanDeleteSlate,
} from './utils';

function FlexibleDoublePicksTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

export function FlexibleDoublePicksModal({
  isOpen,
  onClose,
  onToggle,
  onSave,
  onValueChange,
  currentValue,
  value,
  slateOptions,
  percentageOptions,
  maxSlates = 10,
  defaultSlate,
  label,
  description,
}: FlexibleDoublePicksModalProps) {
  const { t } = useTranslation('contest');
  const [slates, setSlates] = useState<SlateConfiguration[]>(value);
  const [errors, setErrors] = useState<Record<string, { slateId?: string; percentage?: string }>>(
    {}
  );

  const handleSlateChange = (slateId: string, newSlate: string) => {
    const updatedSlates = slates.map((slate) =>
      slate.id === slateId ? { ...slate, slateId: newSlate } : slate
    );

    setSlates(updatedSlates);

    const changedSlate = updatedSlates.find((s) => s.id === slateId);
    if (changedSlate && changedSlate.percentage !== null) {
      onSave(updatedSlates);
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[slateId]) {
        delete newErrors[slateId].slateId;
        if (Object.keys(newErrors[slateId]).length === 0) {
          delete newErrors[slateId];
        }
      }
      return newErrors;
    });
  };

  const handlePercentageChange = (slateId: string, percentage: number) => {
    const changedSlateIndex = slates.findIndex((slate) => slate.id === slateId);

    const updatedSlates = slates.map((slate, index) => {
      if (slate.id === slateId) {
        return { ...slate, percentage };
      }

      if (
        index > changedSlateIndex &&
        slate.percentage !== null &&
        slate.percentage >= percentage
      ) {
        return { ...slate, percentage: null };
      }

      return slate;
    });

    setSlates(updatedSlates);

    const changedSlate = updatedSlates.find((s) => s.id === slateId);
    if (changedSlate && changedSlate.slateId) {
      onSave(updatedSlates);
    }

    setErrors((prev) => {
      const newErrors = { ...prev };

      if (newErrors[slateId]) {
        delete newErrors[slateId].percentage;
        if (Object.keys(newErrors[slateId]).length === 0) {
          delete newErrors[slateId];
        }
      }

      updatedSlates.forEach((slate, index) => {
        if (index > changedSlateIndex && slate.percentage === null && newErrors[slate.id]) {
          delete newErrors[slate.id].percentage;
          if (Object.keys(newErrors[slate.id]).length === 0) {
            delete newErrors[slate.id];
          }
        }
      });

      return newErrors;
    });
  };

  const handleAddSlate = () => {
    const { canAdd } = checkCanAddSlate(slates, slateOptions, percentageOptions, maxSlates);
    if (!canAdd) return;

    const nextSlateValue = getNextSlateValue(slates, slateOptions, defaultSlate);

    const newSlate: SlateConfiguration = {
      id: `slate-${Date.now()}`,
      slateId: nextSlateValue,
      percentage: null,
    };

    const updatedSlates = [...slates, newSlate];
    setSlates(updatedSlates);
    onSave(updatedSlates);
  };

  const handleDeleteSlate = (slateId: string) => {
    const updatedSlates = slates.filter((slate) => slate.id !== slateId);

    const completeSlatesAfterDeletion = filterCompleteSlates(updatedSlates);

    if (completeSlatesAfterDeletion.length === 0) {
      const currentCompleteSlates = filterCompleteSlates(slates);

      if (currentCompleteSlates.length > 0) {
        return;
      }
    }

    setSlates(updatedSlates);

    if (completeSlatesAfterDeletion.length > 0) {
      onSave(updatedSlates);
    } else {
      onValueChange?.({
        ...currentValue,
        slates: [],
      });
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[slateId];
      return newErrors;
    });
  };

  const { canAdd } = checkCanAddSlate(slates, slateOptions, percentageOptions, maxSlates);

  return (
    <Drawer
      title={<FlexibleDoublePicksTitle label={label} />}
      onClose={onClose}
      onToggle={onToggle}
      open={isOpen}
    >
      {description ? (
        <Stack>
          <Text c="gray2.3" ta="center" variant="body-large">
            {description}
          </Text>
        </Stack>
      ) : null}
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24}>
        {slates.map((slate, index) => (
          <div key={slate.id}>
            {index > 0 && <Divider />}
            <SlateConfigurationRow
              slate={slate}
              slateOptions={slateOptions}
              percentageOptions={percentageOptions}
              onSlateChange={handleSlateChange}
              onPercentageChange={handlePercentageChange}
              onDelete={handleDeleteSlate}
              canDelete={checkCanDeleteSlate(slate, slates)}
              errors={errors[slate.id]}
              index={index}
              allSlates={slates}
            />
          </div>
        ))}

        {canAdd && (
          <LinkButton size="medium" onClick={handleAddSlate}>
            {t('manage.common.fields.flexibleDoublePicksField.addAnotherWeek')}
          </LinkButton>
        )}
      </Stack>
    </Drawer>
  );
}
